// Which backend the client draws through, and why.
//
// The choice is made ONCE, before the canvas is touched. A canvas that gave
// a `webgpu` context refuses `webgl2` after, so the engine asks for a device
// first and claims the canvas last.
//
// The rule the owner set on 2026-09-11: WebGPU where the browser gives a
// device, WebGL2 everywhere else, and no notice. A forced `?renderer=webgpu`
// on a browser with no device falls back with one `console.warn`, because
// the page then draws a picture the operator did not ask for and the stats
// line alone is a thin way to say so.
//
// `pickBackend` is PURE: it takes the probe as an argument, so jsdom tests
// the whole matrix without a device. `fast/gpu/device.ts` is the one file
// that reads `navigator.gpu`.

import type { GpuBundle } from '../gpu/device';

/** What the `?renderer=` knob can say. */
export type RendererPref = 'auto' | 'webgpu' | 'webgl2';

/**
 * What the client asks for when the page states nothing.
 *
 * It is `auto`: the client takes WebGPU where the browser gives a device and
 * WebGL2 everywhere else. The picture was completed on both backends on
 * 2026-09-11, and `?renderer=webgl2` is the way back to the WebGL2 one.
 */
export const DEFAULT_RENDERER: RendererPref = 'auto';

// The bundle is declared in `../gpu/device.ts` and only named here. Every
// WebGPU type stays inside that folder, which `seam.test.ts` pins, and the
// import is type-only, so this file carries no backend at run time.
export type { GpuBundle };

/**
 * Why the pick landed on WebGL2. The warning reads it.
 *
 * `no-device` covers a browser that has no `navigator.gpu` and an adapter
 * that refused a device: the probe answers null for both, and this file must
 * not read `navigator` itself.
 */
export type GlReason = 'asked' | 'no-device' | 'threw';

export type Pick =
  | { kind: 'webgpu'; bundle: GpuBundle }
  | { kind: 'webgl2'; why: GlReason };

/**
 * Picks the backend from the preference and one probe of the browser.
 *
 * The probe answers a bundle or null. It must not throw; a probe that
 * throws all the same is caught here, because a broken adapter must not
 * stop the client from drawing.
 */
export async function pickBackend(
  pref: RendererPref,
  probe: () => Promise<GpuBundle | null>,
): Promise<Pick> {
  if (pref === 'webgl2') return { kind: 'webgl2', why: 'asked' };
  let bundle: GpuBundle | null = null;
  try {
    bundle = await probe();
  } catch {
    return { kind: 'webgl2', why: 'threw' };
  }
  if (bundle) return { kind: 'webgpu', bundle };
  return { kind: 'webgl2', why: 'no-device' };
}
